import { UserCapabilities } from '@vimeo/core/hooks/use-capability';
import { Definable, EmptyObject, Optional } from '@vimeo/core/types/generic';

import { IVimeoApiConnection, IVimeoApiLink, TVimeoApiConnectionExtended } from './api';
import { EEventCompletionStatus, ILiveQuota, IThumbnailPicture } from './event';
import { IGuestSeat } from './guest';
import { EUserEntitlement, EUserPreference } from './user';

export enum EComposerApplicationType {
  UNKNOWN = 'unknown',
  BROADCASTER = 'broadcaster',
  INTERACTION_TOOLS = 'interaction_tools',
  RTMP = 'rtmp',
  GUEST = 'guest',
  CREATE = 'create',
  INTERVIEW = 'interview',
  SELECT = 'select',
  COMPLETED = 'completed',
}

export enum EComposerStreamModeType {
  LIVE = 'live',
  SIMULIVE = 'simulive',
  RECORD = 'record',
}

export enum ERecordState {
  PLAYING = 'playing',
  NOT_ACTIVE = 'not-active',
}

export enum ERecordStateCommand {
  PAUSE = 'pause_recording',
  RESUME = 'resume_recording',
  RESTART = 'restart_recording',
  DELETE = 'delete_recording',
}

export enum EComposerSessionType {
  UNKNOWN = 'unknown',
  LIVE_EVENT = 'live_event',
  VENUE = 'venue',
  INTERVIEW = 'interview',
}

export type TComposerSessionId = string | number;

export type TComposerSessionCode = string | number;

export interface IComposerSessionThumbnail {
  active: boolean;
  baseLink: string;
  defaultPicture: boolean;
  resourceKey: string;
  sizes: Array<IThumbnailPicture>;
  type: string;
  uri: string;
}

/**
 * Metadata with URIs/connections related to composer session.
 */
export interface IBroadcasterComposerSessionInfoMetadata {
  connections: {
    autoClosedCaptions: IVimeoApiConnection;
    audioTracks: IVimeoApiConnection;
    availableLanguages: IVimeoApiConnection;
    availableDestinations: IVimeoApiConnection;
    capabilities: IVimeoApiConnection;
    destinations: IVimeoApiConnection;
    firebase: IVimeoApiConnection;
    guest: IVimeoApiConnection;
    guests: IVimeoApiConnection;
    ingestStatus: IVimeoApiConnection;
    interactionTools: IVimeoApiConnection;
    latestVideo: IVimeoApiConnection;
    ottChannels: IVimeoApiConnection;
    ottDestination: IVimeoApiConnection;
    ottDestinations: IVimeoApiConnection;
    ottMetadata: IVimeoApiConnection;
    owner: IVimeoApiConnection;
    preferences: IVimeoApiConnection;
    rtc: IVimeoApiConnection;
    rtmpCredentials: IVimeoApiConnection;
    rtmpSignalingCredentials: IVimeoApiConnection;
    settings: IVimeoApiConnection;
    streamHealth: IVimeoApiConnection;
    teamMember: TVimeoApiConnectionExtended<{ role?: string }>;
    viewerStats: IVimeoApiConnection;
  };
  interactions: {
    inviteGuest: IVimeoApiConnection;
    activateSession: IVimeoApiConnection;
    startSession: IVimeoApiConnection;
    endSession: IVimeoApiConnection;
    updateSchedule: IVimeoApiConnection;
    updateStreamMode: IVimeoApiConnection;
    updateRecordMode: IVimeoApiConnection;
    updateRtmpPreview: IVimeoApiConnection;
    completeEvent: IVimeoApiConnection;
  };
}

/**
 * Metadata with URIs/connections related to composer session.
 */
export interface IGuestComposerSessionInfoMetadata {
  connections: {
    ingestStatus: IVimeoApiConnection;
    rtc: IVimeoApiConnection;
    firebase: IVimeoApiConnection;
    interactionTools: IVimeoApiConnection;
  };
  interactions: EmptyObject;
}

/**
 * Metadata with URIs/connections related to composer session.
 */
export interface IGuestComposerSessionInfoMetadata {
  connections: {
    ingestStatus: IVimeoApiConnection;
    rtc: IVimeoApiConnection;
    firebase: IVimeoApiConnection;
    interactionTools: IVimeoApiConnection;
  };
}

export interface IComposerSessionUser {
  uri: string;
}

export interface IComposerSessionSessionOwnerInfo {
  capabilities: UserCapabilities;
  entitlements: Record<EUserEntitlement, Definable<boolean>>;
  preferences: Record<EUserPreference, Definable<boolean>>;
  user: IComposerSessionUser;
}

/**
 * Session details, generic for all types and including all essential information.
 */
export interface IComposerSessionInfo {
  uri: string;
  title: string;
  timeZone: string;
  status: Optional<EEventCompletionStatus>;
  viewLink: IVimeoApiLink;
  settingsLink: IVimeoApiLink;
  baseLink: IVimeoApiLink;
  rtcPreviewLink?: IVimeoApiLink;
  rtmpsPreviewLink?: IVimeoApiLink;
  appearanceLink?: IVimeoApiLink;
  registrationFormLink?: IVimeoApiLink;
  registrationEmailLink?: IVimeoApiLink;
  registrationTableLink?: IVimeoApiLink;
  liveQuota: ILiveQuota;
  thumbnail: IComposerSessionThumbnail;
  owner: IComposerSessionSessionOwnerInfo;
  metadata: IBroadcasterComposerSessionInfoMetadata;
}

export interface IGuestComposerSessionInfo {
  seat: IGuestSeat;
  sessionName: string;
  sessionIsDemo: boolean;
  nextOccurrenceTime: Optional<string>;
  metadata: IGuestComposerSessionInfoMetadata;
}

export enum EExternalModule {
  FIREBASE = 'firebase',
  FIRESTORE = 'firestore',
  AGORA = 'agora',
}
